// Laser Add Sheet: the laser dock decides the lot (jobq-api "laser dock rule", 2026-10-21).
//
// Mike: "The laser task calls for a specific material. The operator docks the beacon for that
// material. JobQ verifies the correct material is docked, and all sheet deductions are enforced by
// this docking." The API owns the rule (POST /laser/{id}/increment_sheet refuses with a 409
// `dock_required`); GET /laser/{id}/dock_check runs the SAME computation for the screen, so the
// dialog and the save never disagree. This file is the dialog's read + rules (pure, no React);
// `useLaserDockCheck.js` polls it.
//
// An API without the endpoint (404), or an answer without `required`, leaves the dialog exactly as
// it was before the rule.

import { beaconFetch, trkText, parseLotNumber } from '../../utils/beacons';
import { visibleLen } from '../../utils/tmPricing';

/** How often the open dialog re-reads the dock (plugging / unplugging fixes it on the spot). */
export const DOCK_POLL_MS = 2000;
/** While the dock does not decide this task (`required` false): a slower poll (the rule may switch on). */
export const DOCK_SLOW_POLL_MS = 10000;
/** A refused / failed read (401, 403, 429, 5xx, no answer) backs off: 2 -> 4 -> 8 s, never past this. */
export const DOCK_BACKOFF_MAX_MS = 10000;
/** An ok card with no fresh answer for this long is stale: "Checking the dock…", Confirm disabled. */
export const DOCK_STALE_MS = 10000;
/** No input in the dialog for this long: polling stops (any input or focus starts it again). */
export const DOCK_IDLE_MS = 10 * 60 * 1000;
/** The statuses that mean "this API has no dock_check": the dialog is today's, polling stops. */
export const DOCK_MISSING_STATUSES = Object.freeze([400, 404, 405]);

/** The API's minimum for an override reason (tm_pricing.visible_len >= 5). */
export const DOCK_OVERRIDE_REASON_MIN = 5;

/**
 * GET /laser/{task_id}/dock_check. Never throws. Resolves
 *  - {check}            an answer (any 2xx JSON object),
 *  - {missing: true}    the API has no such endpoint (400 / 404 / 405: an older API; polling stops),
 *  - {failed: true, status}  anything else (401, 403, 429, 5xx, no answer): keep what we had, back off.
 */
export async function loadDockCheck(taskId) {
  const r = await beaconFetch(`/laser/${encodeURIComponent(taskId)}/dock_check`);
  if (r.ok && r.body && typeof r.body === 'object') return { check: r.body };
  if (DOCK_MISSING_STATUSES.includes(r.status)) return { missing: true };
  return { failed: true, status: r.status };
}

/** The wait before the next read after `failures` refused / failed reads in a row (0 = the normal poll). */
export function dockRetryDelay(failures, pollMs = DOCK_POLL_MS) {
  if (!failures) return pollMs;
  return Math.min(pollMs * (2 ** failures), Math.max(DOCK_BACKOFF_MAX_MS, pollMs));
}

/** The rule is on and this task's material must come from the dock: the dialog is the dock's. */
export function dockEnforced(check) {
  return !!(check && check.mode === 'enforce' && check.required === true && check.verdict !== 'not_required');
}

/** The switch is off but the dock would decide: show the card as advice, keep today's controls. */
export function dockAdvice(check) {
  return !!(check && check.mode !== 'enforce' && check.required === true && check.verdict !== 'not_required');
}

/** The lot an `ok` verdict records the sheet against (docked_lot.mtn_id), else null. */
export function dockLotToSend(check) {
  if (!check || check.verdict !== 'ok' || !check.docked_lot) return null;
  const id = Number(check.docked_lot.mtn_id);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** Confirm & Add may go ahead on the dock alone (the manager override is the other way). */
export function dockAllowsSave(check) {
  return dockLotToSend(check) !== null;
}

function pallets(check) {
  return check && Array.isArray(check.pallets) ? check.pallets.filter((p) => p && typeof p === 'object') : [];
}

function trkOf(p) {
  if (p.trk) return String(p.trk);
  return p.mtn_id != null ? trkText(p.mtn_id) : null;
}

function leftText(n) {
  const v = Number(n);
  if (n === null || n === undefined || n === '' || !Number.isFinite(v)) return null;
  return `${v % 1 === 0 ? v : v.toFixed(2)} left`;
}

/**
 * Who a pallet's sheets are earmarked for (contract item 10: `earmarked_for` [{sales_order_id, order_number,
 * customer_name}], `mine` = earmarked for THIS task's order): "earmarked for this job's order (SO-1138)",
 * "earmarked for SO-1138, SO-1140", or null when nothing is earmarked.
 */
function earmarkOrders(p) {
  const seen = new Map();
  for (const e of (Array.isArray(p.earmarked_for) ? p.earmarked_for : [])) {
    if (!e || typeof e !== 'object') continue;
    const name = e.order_number || (e.sales_order_id != null ? `SO #${e.sales_order_id}` : null);
    if (!name || seen.has(name)) continue;
    const held = Number(e.held);
    seen.set(name, e.held !== null && e.held !== undefined && e.held !== '' && Number.isFinite(held) ? held : null);
  }
  return [...seen].map(([name, held]) => ({ name, held }));
}

export function earmarkText(p) {
  if (!p) return null;
  const list = earmarkOrders(p).map((o) => o.name).join(', ');
  if (p.mine === true) return `earmarked for this job's order${list ? ` (${list})` : ''}`;
  return list ? `earmarked for ${list}` : null;
}

/**
 * Earmarks hold SHEETS, not pallets (contract item 10a, Mike 2026-10-21): with `free_sheets` (the sheets left
 * that no other order holds) and `earmarked_for[].held`, "10 left, 2 earmarked for SO-1138 (8 free for this
 * job)"; several orders "10 left, 2 earmarked for SO-1138, 3 for SO-1140 (5 free for this job)". Null when the
 * API sends no `free_sheets`, nothing is earmarked, or the pallet is this job's order's own (`mine`).
 */
export function sheetsLeftText(p) {
  if (!p || p.mine === true) return null;
  const free = Number(p.free_sheets);
  if (p.free_sheets === null || p.free_sheets === undefined || p.free_sheets === '' || !Number.isFinite(free)) return null;
  const orders = earmarkOrders(p);
  if (orders.length === 0) return null;
  const held = orders.map((o, i) => {
    const n = o.held !== null ? `${o.held} ` : '';
    return i === 0 ? `${n}earmarked for ${o.name}` : `${n}for ${o.name}`;
  }).join(', ');
  const left = leftText(p.remaining);
  const freeText = free > 0 ? `${free} free for this job` : 'none free for this job';
  return `${left ? `${left}, ` : ''}${held} (${freeText})`;
}

/** One pallet as a line: "BCN-003 · TRK 000409 · 3/16 A36 HRPO 60 x 120 · Heat 12345 · 10 left"
 *  (+ " · earmarked for SO-1138" when its sheets are earmarked; with the API's free_sheets the count reads
 *  "10 left, 2 earmarked for SO-1138 (8 free for this job)"). */
export function palletLine(p, { withLeft = true } = {}) {
  if (!p) return '';
  const bits = [];
  if (p.beacon_id) bits.push(p.beacon_id);
  const trk = trkOf(p);
  if (trk) bits.push(`TRK ${trk}`);
  if (p.material) bits.push(String(p.material));
  if (p.heat) bits.push(`Heat ${p.heat}`);
  const sheets = withLeft ? sheetsLeftText(p) : null;
  if (sheets) {
    bits.push(sheets);
    return bits.join(' · ');
  }
  if (withLeft) {
    const left = leftText(p.remaining);
    if (left) bits.push(left);
  }
  const earmark = earmarkText(p);
  if (earmark) bits.push(earmark);
  return bits.join(' · ');
}

/** The docked pallet an `ok` verdict names (the one on docked_lot), or null. */
export function dockedPallet(check) {
  const id = dockLotToSend(check);
  if (id === null) return null;
  const list = pallets(check);
  return list.find((p) => Number(p.mtn_id) === id) || list[0]
    || { mtn_id: id, trk: check.docked_lot.trk || trkText(id) };
}

/**
 * What the card says: {tone: 'ok' | 'bad', headline, lines[]}.
 *  ok:  "Docked: BCN-003 · TRK 000409 · 3/16 A36 HRPO 60 x 120 · Heat … · 10 left ✓ matches this job"
 *  any other verdict: the API's message, and (several / wrong material / ...) one line per pallet.
 */
export function dockCardView(check) {
  if (!check) return null;
  const ok = dockAllowsSave(check);
  if (ok) {
    const p = dockedPallet(check);
    const matches = p.matches === false ? '' : ' ✓ matches this job';
    return { tone: 'ok', headline: `Docked: ${palletLine(p)}${matches}`, lines: [] };
  }
  const material = check.material && (check.material.description || check.material.code);
  const headline = check.message
    || (check.verdict === 'ok'
      ? 'The dock answered without a pallet to record. Dock the pallet again.'
      : `Dock the beacon of the pallet you're cutting.${material ? ` This job needs ${material}.` : ''}`);
  return { tone: 'bad', headline, lines: pallets(check).map((p) => palletLine(p)) };
}

/** Beacons plugged in with no pallet (charging / updating): never counted. The grey line, or null. */
export function freeBeaconsLine(check) {
  const ids = (check && Array.isArray(check.free) ? check.free : [])
    .map((f) => (f && typeof f === 'object' ? f.beacon_id : f))
    .filter(Boolean);
  if (ids.length === 0) return null;
  return ids.length === 1
    ? `${ids[0]} is plugged in with no pallet (charging): not counted.`
    : `${ids.join(', ')} are plugged in with no pallet (charging): not counted.`;
}

/**
 * The save's 409 refusal (increment_sheet detail {error: 'dock_required', reason, message, material,
 * pallets, free}) from the response text, or null for any other answer.
 */
export function dockRefusal(text) {
  try {
    const body = typeof text === 'string' ? JSON.parse(text) : text;
    const d = body && body.detail;
    if (d && typeof d === 'object' && d.error === 'dock_required') {
      return {
        reason: d.reason || null,
        message: d.message || "Dock the beacon of the pallet you're cutting.",
        material: d.material || null,
        pallets: Array.isArray(d.pallets) ? d.pallets : [],
        free: Array.isArray(d.free) ? d.free : [],
      };
    }
  } catch (_) { /* not JSON */ }
  return null;
}

/**
 * The manager override's fields (dead dock / dead beacon), or {error} to show: a manager PIN, a
 * reason of at least 5 visible characters and the tracking number of the pallet being cut, or
 * `noLot` ("This piece has no tracking number": mtnId null; the API takes one manager approval for
 * the dock AND the no-tracking guard).
 */
export function dockOverrideFields(managerPin, reason, trackingText, { noLot = false } = {}) {
  const pin = String(managerPin || '').trim();
  const why = String(reason || '').trim();
  if (!pin) return { error: 'A manager PIN is required to override the laser dock.' };
  if (visibleLen(why) < DOCK_OVERRIDE_REASON_MIN) {
    return { error: `Say why the dock is being overridden (at least ${DOCK_OVERRIDE_REASON_MIN} characters).` };
  }
  if (noLot) return { dockOverridePin: pin, dockOverrideReason: why, mtnId: null };
  const mtnId = parseLotNumber(trackingText);
  if (!mtnId) {
    return { error: "Enter the tracking number of the pallet you're cutting, or tick \"This piece has no tracking number\"." };
  }
  return { dockOverridePin: pin, dockOverrideReason: why, mtnId };
}

const BLOCKED_LABELS = Object.freeze({
  none: 'Dock the pallet to continue',
  several: 'Unplug the extra pallet',
  wrong_material: 'Dock the right pallet',
  use_earmarked: 'Dock the earmarked pallet',
  earmarked_other: 'Dock another pallet',
  not_docked_lot: 'Dock the pallet to continue',
  dock_offline: "The dock isn't reporting",
});

/** What a Confirm & Add the dock holds back says instead (enforced, no override). */
export function dockBlockedLabel(check, { stale = false } = {}) {
  if (!check || stale) return 'Checking the dock…';
  return BLOCKED_LABELS[check.verdict] || 'Dock the pallet to continue';
}
